import React, { useState, useEffect, useRef } from 'react';
import { PaperclipIcon, Trash2, Download, Upload, X, AlertTriangle, Search, Plus, SortAsc, SortDesc, Edit2 } from 'lucide-react';
// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';
import { validateFiles, formatFileSize, MAX_FILE_SIZE, ALLOWED_EXTENSIONS } from './fileValidationUtils';


// REPLACE existing MainDialogStyles with:
const MainDialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    borderRadius: '8px',
    maxWidth: '95vw',
    width: '1200px',
    height: '90vh',
    maxHeight: '90vh',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  header: {
    backgroundColor: '#CCCCFF',
    textAlign: 'center',
    padding: '15px 20px',
    borderRadius: '8px 8px 0 0',
    fontSize: '18px',
    fontWeight: 'bold',
    flexShrink: 0
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px'
  },
  footer: {
    padding: '15px 20px',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    borderRadius: '0 0 8px 8px',
    flexShrink: 0
  }
};


const Dialog = ({ message, onClose, onCancel }) => {
  const isConfirmation = message.startsWith('Are you sure');
  const isEmptyUseCases = message.startsWith('No use cases found');

  const formattedMessage = message.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < message.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));

  return (
    <div style={MainDialogStyles.overlay}>
      <div style={{
        ...MainDialogStyles.dialog,
        borderRadius: isEmptyUseCases ? '16px' : '8px',
        border: isEmptyUseCases ? '1px solid #ccc' : 'none',
      }}>
        <h2 style={{
          ...MainDialogStyles.title,
          fontSize: isEmptyUseCases ? '16.8px' : '18px',
          lineHeight: isEmptyUseCases ? '1.4' : '1.2',
        }}>
          {formattedMessage}
        </h2>
        {isConfirmation ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              style={{
                ...MainDialogStyles.button,
                backgroundColor: '#FF8F89',
                color: 'white',
                width: '50%'
              }}
              onClick={onClose}
            >
              Yes
            </button>
            <button
              style={{
                ...MainDialogStyles.button,
                width: '50%'
              }}
              onClick={onCancel}
            >
              No
            </button>
          </div>
        ) : (
          <button
            style={{
              ...MainDialogStyles.button,
              backgroundColor: isEmptyUseCases ? '#FFFDD0' : '#FFFDD0',
              width: isEmptyUseCases ? '120px' : '100%',
              margin: isEmptyUseCases ? '0 auto' : '0',
              display: 'block'
            }}
            onClick={onClose}
          >
            OK
          </button>
        )}
      </div>
    </div>
  );
};

const ManageUseCasesDialog = ({
  isOpen,
  onClose,
  selectedRequirements,
  selectedLevelId,
  corporateAccount,
  currentProject,
  login_token,
  requirementIdWithPrefix,
  functionalLevelDescription
}) => {
  const [useCases, setUseCases] = useState([]);
  const [selectedUseCases, setSelectedUseCases] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [useCaseLevel, setUseCaseLevel] = useState('ProjectLevel');
  const [useCaseForm, setUseCaseForm] = useState({
    usecase_description: '',
    acceptance_criteria: '',
    status: ''
  });
  const [editUseCaseId, setEditUseCaseId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState([]);
  const [sortCriteria, setSortCriteria] = useState('updated_date desc');
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // New state variables for attachments
  const [files, setFiles] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
  const fileInputRef = useRef(null);

  // New state for dialog system
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [pendingDeleteAction, setPendingDeleteAction] = useState(null);

// ADD these new state variables:
const [createSuccess, setCreateSuccess] = useState(null);
const [attachmentUploadFailed, setAttachmentUploadFailed] = useState(false);

const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);


  // New permissions state
  const [permissions, setPermissions] = useState({
    canCreateEdit: false,
    canDelete: false
  });

  // Permissions check function
  const checkPermission = async (categoryHeader, categorySubHeaders) => {
    try {
      const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          user_id: localStorage.getItem('user_id'),
          project_id: currentProject,
          category_header: categoryHeader,
          category_sub_header: categorySubHeaders
        }),
      });
      const data = await response.json();
      console.log('API Response:', data);

      // Create new permissions object
      const newPermissions = {
        canCreateEdit: false,
        canDelete: false
      };

      // Map API response to permission fields
      data.user_actions_list.forEach(item => {
        switch(item.user_action) {
          case 'Create or Edit Usecases':
            newPermissions.canCreateEdit = item.has_access;
            break;
          case 'Delete Usecases':
            newPermissions.canDelete = item.has_access;
            break;
        }
      });

      console.log('New Permissions:', newPermissions);

      // Update the state with the new permissions
      setPermissions(newPermissions);

      return data.access_status && data.status === 'Success';
    } catch (error) {
      console.error(`Error checking permission for ${categoryHeader}:`, error);
      return false;
    }
  };

  // Helper function to ensure data is fully reset
  const resetAllData = () => {
    setUseCases([]);
    setSelectedUseCases(new Set());
    setError('');
    setIsLoading(false);
    setSearchTerm('');
  };

  const showErrorDialog = (message) => {
    setDialogMessage(message);
    setShowDialog(true);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    if (pendingDeleteAction) {
      pendingDeleteAction();
      setPendingDeleteAction(null);
    }
  };

  const handleDialogCancel = () => {
    setShowDialog(false);
    setPendingDeleteAction(null);
  };




  // AttachmentList component for displaying attachments
  const AttachmentList = ({ attachments, onDelete, onDownload, isLoading }) => {
    if (isLoading) {
      return <div className="mt-2 text-sm text-gray-500">Loading attachments...</div>;
    }

    if (!attachments || attachments.length === 0) {
      return <div className="mt-2 text-sm text-gray-500">No attachments</div>;
    }

    return (
      <div className="mt-2 space-y-2">
        {attachments.map(attachment => (
          <div
            key={attachment.attachment_id}
            className="flex items-center justify-between p-2 bg-gray-50 rounded border"
          >
            <div className="flex items-center">
              <PaperclipIcon className="h-4 w-4 mr-2 text-gray-500" />
              <span className="text-sm truncate mr-2 max-w-xs">{attachment.file_name}</span>
              <span className="text-xs text-gray-500">
                ({formatFileSize(attachment.file_size)})
              </span>
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => onDownload(attachment.attachment_id)}
                className="p-1 text-blue-600 hover:text-blue-800 rounded"
              >
                <Download className="h-4 w-4" />
              </button>
              <button
                type="button"



                onClick={() => {
                  console.log('Delete button clicked for attachment:', attachment.attachment_id);
                  onDelete(attachment.attachment_id);
                }}



                className="p-1 text-red-600 hover:text-red-800 rounded"
                disabled={!permissions.canCreateEdit}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Fetch use case attachments
  const fetchAttachments = async (usecaseId) => {
    if (!usecaseId) return;

    setIsLoadingAttachments(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_requirement_attachments'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          req_id: `uc-${usecaseId}`
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setAttachments(data.attachments || []);
      } else {
        console.error('Failed to fetch attachments:', data.status_description);
      }
    } catch (error) {
      console.error('Error fetching attachments:', error);
    } finally {
      setIsLoadingAttachments(false);
    }
  };

  // File handling functions
const handleFileChange = (e) => {
  const selectedFiles = Array.from(e.target.files);

  const validationResult = validateFiles(selectedFiles);
  if (!validationResult.isValid) {
    setError(`File validation failed: ${validationResult.errors.join('; ')}`);
    e.target.value = ''; // Fixed: changed 'event' to 'e'
    return;
  }

  setError('');
  setFiles(prevFiles => [...prevFiles, ...selectedFiles]);
};

  const removeFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  // Delete attachment
  const handleDeleteAttachment = async (attachmentId) => {
  console.log('Deleting attachment:', attachmentId);
    try {
      const response = await fetch(appConfig.getUrl('/delete_requirement_attachment'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          attachment_id: attachmentId
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        // Update the UI by removing the deleted attachment
        setAttachments(prevAttachments =>
          prevAttachments.filter(attachment => attachment.attachment_id !== attachmentId)
        );
      } else {
        setError(`Failed to delete attachment: ${data.status_description}`);
      }
    } catch (error) {
      setError(`Error deleting attachment: ${error.message}`);
    }
  };

  // Download attachment
  const handleDownloadAttachment = (attachmentId) => {
    // Create a download link that includes the auth token in headers

        const downloadUrl = appConfig.getUrl(`/download_requirement_attachment/${attachmentId}`);


    // Create a hidden form to post the download request with the token
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = downloadUrl;
    form.target = '_blank';

    // Add token as hidden input
    const tokenInput = document.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = 'token';
    tokenInput.value = login_token;
    form.appendChild(tokenInput);

    // Add to body, submit, and remove
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };




  // Upload attachments
const uploadAttachments = async (usecaseId) => {
  console.log('check 1');

  if (files.length === 0) return true;
  console.log('check 2');

  // ADD FILE VALIDATION HERE - This is the new part
  const validationResult = validateFiles(files);
  if (!validationResult.isValid) {
    setError(`File validation failed: ${validationResult.errors.join(', ')}`);
    return false;
  }

  setIsUploading(true);

  const formData = new FormData();
  formData.append('req_id', `uc-${usecaseId}`);
  formData.append('corporate_account', corporateAccount);
  formData.append('project_id', currentProject);
  formData.append('user_id', localStorage.getItem('user_id') || '');

  files.forEach(file => {
    formData.append('files[]', file);
  });

  console.log('check 3');

  try {
    const response = await fetch(appConfig.getUrl('/upload_requirement_attachment'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${login_token}`
      },
      body: formData
    });

    const data = await response.json();
    console.log('check 4');

    // ENHANCED ERROR HANDLING - Add this check
    if (response.status === 413) {
      setError('Use case saved. File not attached due to large size. Maximum file size allowed is 16MB');
      throw new Error('File too large. Maximum file size allowed is 16MB');
    }

    if (data.status === 'Success') {
      // Clear the selected files after successful upload
      setFiles([]);

      // If in edit mode, refresh the attachments list
      if (isEditDialogOpen) {
        fetchAttachments(usecaseId);
      }

      return true;
    } else {
      console.log('check 5');
      throw new Error(data.status_description || 'Failed to upload attachments');
    }
  } catch (error) {
    console.error('Upload error:', error);

    // ENHANCED ERROR MESSAGES - Replace your existing catch block with this
        if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
          setError('Network error: Please check your internet connection and try again. Please close this dialog and edit the use case to retry uploading attachments.');
        } else if (error.message.includes('413') || error.message.includes('too large')) {
          setError('Use case saved, but file not attached. Please reduce file size and try again (Maximum: 10MB per file). Please close this dialog and edit the use case to retry uploading attachments.');
        } else {
          setError(`Error uploading attachments: ${error.message}. Please close this dialog and edit the use case to retry uploading attachments.`);
        }

    return false;
  } finally {
    setIsUploading(false);
  }
};

  // Delete selected use cases
  // Delete confirmation dialog handler
const handleDeleteUseCasesConfirm = () => {
  if (selectedUseCases.size === 0) {
    showErrorDialog('Please select at least one use case to delete');
    return;
  }

  if (!permissions.canDelete) {
    return; // Don't proceed if no permission
  }

  setIsDeleteConfirmOpen(true);
};

// Actual delete function
const handleDeleteUseCases = async () => {
  setIsDeleteConfirmOpen(false);
  setIsLoading(true);
  setError('');

  try {
    const usecaseIds = Array.from(selectedUseCases);

    const response = await fetch(appConfig.getUrl('/delete_requirement_usecases'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        usecase_ids: usecaseIds
      })
    });

    const data = await response.json();

    if (data.status === 'Success') {
      setError('Use cases deleted successfully');
      setSelectedUseCases(new Set());

      // Delay the refresh to allow user to see the success message
      setTimeout(() => {
        setError('');
        fetchUseCases(useCaseLevel);
      }, 2000);
    } else {
      throw new Error(data.status_description || 'Failed to delete use cases');
    }
  } catch (error) {
    setError(error.message);
  } finally {
    setIsLoading(false);
  }
};

  // Fetch statuses from API
  const fetchStatuses = async () => {
    try {
      const response = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          entity: 'REQUIREMENT_USECASE',
          search_query: ''
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        const statusValues = data.entity_statuses.map(item => item.status);
        setAvailableStatuses(statusValues);

        // Set initial status to first available status if there are any
        if (statusValues.length > 0) {
          setUseCaseForm(prev => ({
            ...prev,
            status: statusValues[0]
          }));

          // Update filter status to include ALL available statuses by default
          setFilterStatus([...statusValues]);

          return statusValues; // Return statuses for immediate use
        }
      } else {
        throw new Error(data.status_description || 'Failed to fetch statuses');
      }

      return []; // Return empty array if no statuses
    } catch (err) {
      console.error('Error fetching statuses:', err);
      setError('Failed to load status options');
      return []; // Return empty array on error
    }
  };

  // Initialize component when dialog opens
  useEffect(() => {
    const initializeDialog = async () => {
      if (isOpen && !isInitialized) {
        // Reset all data
        resetAllData();
        setIsLoading(true);

        // Check permissions first
        await checkPermission('Test Management', ['Create or Edit Usecases', 'Delete Usecases']);

        // Determine the initial use case level based on selections
        let initialLevel = 'ProjectLevel';
        if (selectedRequirements.size === 1) {
          initialLevel = 'RequirementLevel';
        } else if (selectedLevelId) {
          initialLevel = 'FunctionalLevel';
        }

        // Set the use case level
        setUseCaseLevel(initialLevel);

        // Fetch statuses first and wait for them
        const statuses = await fetchStatuses();

        // Now fetch use cases with the updated statuses
        await fetchUseCases(initialLevel, statuses, '');

        setIsInitialized(true);
      } else if (!isOpen) {
        // Reset initialization when dialog closes
        setIsInitialized(false);
      }
    };

    initializeDialog();
  }, [isOpen]);

  // Handle search input with Enter key support
  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = () => {
    // Trigger refresh of use cases list
    fetchUseCases(useCaseLevel, filterStatus, searchTerm);
  };





// Updated global click handler to include all filter dropdowns
useEffect(() => {
  const handleClickOutside = (event) => {
    // List all dropdown IDs that need click-outside handling
    const dropdownIds = [
      'status-filter-dropdown',
      'criticality-filter-dropdown',
      'priority-filter-dropdown',
      'source-systems-filter-dropdown',
      'products-filter-dropdown'
    ];

    dropdownIds.forEach(dropdownId => {
      const dropdown = document.getElementById(dropdownId);

      if (dropdown && dropdown.style.display === 'block') {
        const isClickInside = dropdown.contains(event.target);

        // For products dropdown, check the specific button
        let isClickOnToggle = false;
        if (dropdownId === 'products-filter-dropdown') {
          const toggleButton = document.getElementById('products-filter-button');
          isClickOnToggle = toggleButton && toggleButton.contains(event.target);
        } else {
          // For other dropdowns, use the previous sibling approach
          const toggleButton = dropdown.previousElementSibling;
          isClickOnToggle = toggleButton && toggleButton.contains(event.target);
        }

        // Close dropdown if click is outside both dropdown and toggle button
        if (!isClickInside && !isClickOnToggle) {
          dropdown.style.display = 'none';
        }
      }
    });
  };

  // Use 'mousedown' instead of 'click' for better handling
  document.addEventListener('mousedown', handleClickOutside);

  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, []); // Empty dependency array since this should only run once




  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        const dropdown = document.getElementById('status-filter-dropdown');
        if (dropdown && dropdown.style.display === 'block') {
          dropdown.style.display = 'none';
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [availableStatuses]);

  // Effect to handle search term changes - debounced search
  useEffect(() => {
    if (isOpen && isInitialized) {
      const debounceTimer = setTimeout(() => {
        fetchUseCases(useCaseLevel, filterStatus, searchTerm);
      }, 500); // 500ms debounce

      return () => clearTimeout(debounceTimer);
    }
  }, [searchTerm, sortCriteria, isOpen, isInitialized, useCaseLevel, filterStatus]);

  // This effect handles changes in use case level
  useEffect(() => {
    const handleLevelChange = async () => {
      if (isOpen && isInitialized) {
        // Reset all data except for filterStatus
        setUseCases([]);
        setSelectedUseCases(new Set());
        setError('');
        setIsLoading(true);

        // Fetch use cases with current level and statuses, use current search term
        await fetchUseCases(useCaseLevel, filterStatus, searchTerm);
      }
    };

    handleLevelChange();
  }, [useCaseLevel]);

  const fetchUseCases = async (level, statusList = filterStatus, searchQuery = searchTerm) => {
    // Set loading state if not already set
    setUseCases([]);

    if (!isLoading) {
      setIsLoading(true);
    }

    // Determine parameters based on level
    let levelId = null;
    let reqId = null;

    if (level === 'FunctionalLevel') {
      if (!selectedLevelId) {
        setError('Please select a functional level for Functional Level Use Cases');
        setIsLoading(false);
        return;
      }
      levelId = selectedLevelId;
    } else if (level === 'RequirementLevel') {
      if (selectedRequirements.size !== 1) {
        setError('Please select exactly one requirement for Requirement Level Use Cases');
        setIsLoading(false);
        return;
      }
      reqId = Array.from(selectedRequirements)[0];
    }



    try {
      const response = await fetch(appConfig.getUrl('/get_usecase_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          level_id: levelId || '',
          req_id: reqId || '',
          filter_by_status: statusList,
          search_query: searchQuery,
          sort_criteria: sortCriteria
        })
      });

      const data = await response.json();

      if (data.status === 'Success') {
        setUseCases(data.usecase_list || []);
        if (data.usecase_list?.length === 0) {
          showErrorDialog('No use cases found.\nCreate new use cases to get started.');
        }
      } else {
        setUseCases([]);
        // Silently handle error - don't throw
      }
    } catch (err) {
      setUseCases([]);
      setError(err.message);
      console.error('Error fetching use cases:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseCaseLevelChange = (newLevel) => {
    if (newLevel !== useCaseLevel) {
      setUseCaseLevel(newLevel);
    }
  };

  const handleOpenCreateDialog = () => {
    if (!permissions.canCreateEdit) {
      return; // Don't open dialog if no permission
    }
    setUseCaseForm({
      usecase_description: '',
      acceptance_criteria: '',
      status: availableStatuses.length > 0 ? availableStatuses[0] : ''
    });
    setError('');
    setFiles([]);
    setIsCreateDialogOpen(true);
  };

  const handleOpenEditDialog = async (useCaseId) => {
    if (!permissions.canCreateEdit) {
      return; // Don't open dialog if no permission
    }

    setIsLoading(true);
    setError('');
    setEditUseCaseId(useCaseId);
    setFiles([]);
    setAttachments([]);

    try {
      // Fetch use case details first
      const response = await fetch(appConfig.getUrl('/get_usecase_details'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          usecase_id: useCaseId
        })
      });

      const data = await response.json();

      if (data.status === 'Success') {
        const details = data.usecase_details;
        setUseCaseForm({
          usecase_description: details.usecase_description || '',
          acceptance_criteria: details.usecase_acceptance_criteria || '',
          status: details.status || '',
          usecase_id_with_prefix: details.usecase_id_with_prefix || ''
        });

        // Fetch attachments for this use case
        await fetchAttachments(useCaseId);

        setIsEditDialogOpen(true);
      } else {
        throw new Error(data.status_description || 'Failed to fetch use case details');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching use case details:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUseCaseForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateUseCase = async () => {
    setIsLoading(true);
    setError('');

    // Validate input
    if (!useCaseForm.usecase_description) {
      setError('Please enter a use case description');
      setIsLoading(false);
      return;
    }

    // Determine parameters based on level
    let levelId = null;
    let reqId = null;

    if (useCaseLevel === 'FunctionalLevel') {
      if (!selectedLevelId) {
        setError('Please select a functional level');
        setIsLoading(false);
        return;
      }
      levelId = selectedLevelId;
    } else if (useCaseLevel === 'RequirementLevel') {
      if (selectedRequirements.size !== 1) {
        setError('Please select exactly one requirement');
        setIsLoading(false);
        return;
      }
      reqId = Array.from(selectedRequirements)[0];
    }

    try {
      const response = await fetch(appConfig.getUrl('/create_requirement_usecase'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          level_id: levelId || '',
          req_id: reqId || '',
          usecase_description: useCaseForm.usecase_description,
          acceptance_criteria: useCaseForm.acceptance_criteria,
          status: useCaseForm.status
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        if (files.length > 0) {
          const usecaseId = data.usecase_id;
          const uploadSuccess = await uploadAttachments(usecaseId);

          if (!uploadSuccess) {
//            setError('Use case saved but there was an issue uploading attachments.');
            return;
          }
        }

        setError('Use case created successfully');

        // Delay the refresh to allow user to see the success message
        setTimeout(() => {
          setIsCreateDialogOpen(false);
          setUseCaseForm({
            usecase_description: '',
            acceptance_criteria: '',
            status: ''
          });
          setFiles([]);
          setError('');

          // Refresh the use cases list
          fetchUseCases(useCaseLevel);
        }, 2000);
      } else {
        throw new Error(data.status_description || 'Failed to create use case');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error creating use case:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateUseCase = async () => {
    setIsLoading(true);
    setError('');

    // Validate input
    if (!useCaseForm.usecase_description) {
      setError('Please enter a use case description');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(appConfig.getUrl('/update_requirement_usecase'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          usecase_id: editUseCaseId,
          usecase_description: useCaseForm.usecase_description,
          acceptance_criteria: useCaseForm.acceptance_criteria,
          status: useCaseForm.status
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        if (files.length > 0) {
          const uploadSuccess = await uploadAttachments(editUseCaseId);

          if (!uploadSuccess) {
//            setError('Use case updated but there was an issue uploading attachments.');
            return;
          }
        }

        setError('Use case updated successfully');

        // Delay the refresh to allow user to see the success message
        setTimeout(() => {
          setIsEditDialogOpen(false);
          setEditUseCaseId(null);
          setUseCaseForm({
            usecase_description: '',
            acceptance_criteria: '',
            status: ''
          });
          setFiles([]);
          setError('');

          // Refresh the use cases list
          fetchUseCases(useCaseLevel);
        }, 2000);
      } else {
        throw new Error(data.status_description || 'Failed to update use case');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error updating use case:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Create/Edit Use Case Dialog Content
 // Create/Edit Use Case Dialog Content
  const renderUseCaseFormDialog = () => {
    if (!isCreateDialogOpen && !isEditDialogOpen) return null;

    const isEdit = isEditDialogOpen;
    const dialogTitle = isEdit
      ? `Edit Use Case${useCaseForm.usecase_id_with_prefix ? ` - ${useCaseForm.usecase_id_with_prefix}` : ''}`
      : 'Create New Use Case';
    const submitButtonText = isEdit ? 'Update Use Case' : 'Create Use Case';
    const submitHandler = isEdit ? handleUpdateUseCase : handleCreateUseCase;

    return (
      <div style={MainDialogStyles.overlay}>
        <div style={{
          ...MainDialogStyles.dialog,
          width: '700px',
          display: 'flex',
          flexDirection: 'column',
          height: '90vh',
          maxHeight: '90vh'
        }}>
          {/* Fixed Header */}
          <div style={{
            ...MainDialogStyles.header,
            backgroundColor: '#CCCCFF',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            {dialogTitle}
          </div>

          {/* Scrollable Content */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px',
            paddingBottom: '10px'
          }}>
            {/* If Edit Mode, show the ID */}
            {isEdit && useCaseForm.usecase_id_with_prefix && (
              <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm">
                  <strong>Use Case ID:</strong> {useCaseForm.usecase_id_with_prefix}
                </p>
              </div>
            )}

            {/* Level Information - only show for Create */}
            {!isEdit && (
              <div className="mb-6 p-4 border border-gray-300 rounded-md">
                <h3 className="text-base font-semibold mb-3">Adding use case to:</h3>
                {useCaseLevel === 'ProjectLevel' ? (
                  <p>Project Level (Overall project level use cases)</p>
                ) : useCaseLevel === 'FunctionalLevel' ? (
                  <p>Functional Level: <strong>{functionalLevelDescription || 'Select a functional level'}</strong></p>
                ) : (
                  <p>Requirement: <strong>{requirementIdWithPrefix || 'Select a requirement'}</strong></p>
                )}
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); submitHandler(); }}>
              {/* Use Case Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Description: <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="usecase_description"
                    value={useCaseForm.usecase_description}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded h-24"
                    placeholder="Enter use case description"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Acceptance Criteria:
                  </label>
                  <textarea
                    name="acceptance_criteria"
                    value={useCaseForm.acceptance_criteria}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded h-24"
                    placeholder="Enter acceptance criteria"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Status:
                  </label>
                  <select
                    name="status"
                    value={useCaseForm.status}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    disabled={isLoading}
                  >
                    {availableStatuses.length > 0 ? (
                      availableStatuses.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))
                    ) : (
                      <option value="">Loading statuses...</option>
                    )}
                  </select>
                </div>

                {/* Attachments Section */}
                <div className="mt-4 border-t pt-4">
                  <h3 className="text-sm font-medium mb-3"><strong>Attachments</strong></h3>

                  {/* Existing Attachments */}
                  {isEdit && (
                    <div className="mb-3">
                      <h4 className="text-sm font-medium mb-2">Current Attachments:</h4>
                      <AttachmentList
                        attachments={attachments}
                        onDelete={handleDeleteAttachment}
                        onDownload={handleDownloadAttachment}
                        isLoading={isLoadingAttachments}
                      />
                    </div>
                  )}

                  {/* File Upload */}
                  <div className="mb-2">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium mb-1">
                        Upload New Attachments:
                      </label>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                        disabled={isLoading}
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        Select Files
                      </button>
                    </div>

                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className="hidden"
                      multiple
                      disabled={isLoading}
                    />

                    {/* Selected Files List */}
                    {files.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {files.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200"
                          >
                            <div className="flex items-center">
                              <PaperclipIcon className="h-4 w-4 mr-2 text-blue-500" />
                              <span className="text-sm truncate mr-2 max-w-xs">{file.name}</span>
                              <span className="text-xs text-gray-500">
                                ({formatFileSize(file.size)})
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="p-1 text-red-600 hover:text-red-800 rounded"
                              disabled={isLoading}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <p className="mt-1 text-xs text-gray-500">
                      Supported file types: PDF, DOC, DOCX, XLS, XLSX, TXT, PNG, JPG, JPEG, GIF
                    </p>
                  </div>
                </div>
              </div>


            </form>
          </div>

          {/* Fixed Footer */}
          <div style={{
            borderTop: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            padding: '20px',
            flexShrink: 0,
            borderRadius: '0 0 8px 8px'
          }}>


                {error && (
                <div className={'mb-4 p-2 rounded mt-4 ' + (
                  error.includes('successfully')
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-500'
                )}>
                  {error}
                </div>
                )}


            <div className="flex justify-center gap-4">
              <button
                type="button"
                onClick={() => {
                  if (isEdit) {
                    setIsEditDialogOpen(false);
                    setEditUseCaseId(null);
                  } else {
                    setIsCreateDialogOpen(false);
                  }
                  setUseCaseForm({
                    usecase_description: '',
                    acceptance_criteria: '',
                    status: ''
                  });
                  setFiles([]);
                  setError('');
                }}
                style={{
                  backgroundColor: '#FFFDD0',
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={isLoading}
              >
                Close
              </button>

              <button
                type="button"
                onClick={submitHandler}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#C8F4DE',
                  color: 'black',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={isLoading || isUploading}
              >
                {(isLoading || isUploading) ? 'Processing...' : submitButtonText}
              </button>
            </div>


          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  // Generate level title based on current selection
  const getLevelTitle = () => {
    if (useCaseLevel === 'ProjectLevel') {
      return 'Project Level Use Cases';
    } else if (useCaseLevel === 'FunctionalLevel') {
      return 'Functional Level Use Cases' + (functionalLevelDescription ? ' - ' + functionalLevelDescription : '');
    } else {
      return 'Requirement Level Use Cases' + (requirementIdWithPrefix ? ' - ' + requirementIdWithPrefix : '');
    }
  };


// Delete confirmation dialog component
const renderDeleteConfirmDialog = () => {
  if (!isDeleteConfirmOpen) return null;

  return (
    <div style={{...MainDialogStyles.overlay, zIndex: 1100}}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  width: '450px',
                  maxWidth: '90vw',
                  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden'
                }}>

        <div style={{...MainDialogStyles.title, backgroundColor: '#FFE6E6', color: '#CC0000', padding: '15px 0'}}>
          <div className="flex items-center justify-center gap-2">
            <AlertTriangle size={20} />
            <span>Confirm Deletion</span>
          </div>
        </div>

        <div className="px-6 py-4">
          <p className="text-gray-700 mb-4">
            Are you sure you want to delete {selectedUseCases.size} selected use case{selectedUseCases.size !== 1 ? 's' : ''}?
          </p>
          <p className="text-sm text-red-600 mb-4">
            This action cannot be undone.
          </p>

          {/* Show some details about selected use cases */}
          <div className="bg-gray-50 rounded p-3 mb-4 max-h-32 overflow-y-auto">
            <p className="text-xs text-gray-600 mb-2">Selected use cases:</p>
            {Array.from(selectedUseCases).slice(0, 5).map(usecaseId => {
              const useCase = useCases.find(uc => uc.usecase_id === usecaseId);
              return useCase ? (
                <div key={usecaseId} className="text-xs text-gray-700 mb-1">
                  {useCase.usecase_id_with_prefix} - {useCase.usecase_description.length > 50
                    ? `${useCase.usecase_description.substring(0, 50)}...`
                    : useCase.usecase_description}
                </div>
              ) : null;
            })}
            {selectedUseCases.size > 5 && (
              <div className="text-xs text-gray-500">
                ... and {selectedUseCases.size - 5} more
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-center gap-4 p-6 pt-0">
          <button
            onClick={() => setIsDeleteConfirmOpen(false)}
            style={{
              backgroundColor: '#f8f9fa',
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteUseCases}
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Delete {selectedUseCases.size} Use Case{selectedUseCases.size !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

  return (
<div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
<div className="bg-white rounded-lg max-w-screen-xl w-full max-h-[90vh] flex flex-col overflow-hidden">
<h1 className="text-l font-bold text-black bg-[#CCCCFF] text-center flex items-center justify-center rounded-t-lg" style={{ height: '3em' }}>
Manage Usecases
 </h1>

        {/* Use Case Level Selection Box */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-medium mb-4">Please select the level from the below options:</h3>

          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="useCaseLevel"
                className="h-5 w-5 mr-3"
                checked={useCaseLevel === 'ProjectLevel'}
                onChange={() => handleUseCaseLevelChange('ProjectLevel')}
              />
              <span>Project Level Use Cases (Overall project level use cases)</span>
            </label>

            <label className={'flex items-center ' + (!selectedLevelId ? 'opacity-60' : '')}>
              <input
                type="radio"
                name="useCaseLevel"
                className="h-5 w-5 mr-3"
                checked={useCaseLevel === 'FunctionalLevel'}
                onChange={() => handleUseCaseLevelChange('FunctionalLevel')}
                disabled={!selectedLevelId}
              />
              <span>
                Functional Level Use Cases: {selectedLevelId ? functionalLevelDescription : 'No functional level selected'}
              </span>
            </label>

            <label className={'flex items-center ' + (selectedRequirements.size !== 1 ? 'opacity-60' : '')}>
              <input
                type="radio"
                name="useCaseLevel"
                className="h-5 w-5 mr-3"
                checked={useCaseLevel === 'RequirementLevel'}
                onChange={() => handleUseCaseLevelChange('RequirementLevel')}
                disabled={selectedRequirements.size !== 1}
              />
              <span>
                Requirement Level Use Cases: {selectedRequirements.size === 1 ? requirementIdWithPrefix : 'No requirement selected'}
              </span>
            </label>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className={'mb-4 p-2 rounded ' + (
            error.includes('successfully')
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-500'
          )}>
            {error}
          </div>
        )}

        {/* Title bar */}
        <div className="flex items-center mb-4 justify-between bg-blue-100 p-3 rounded">
          {/* Search bar */}
          <div className="flex items-center">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchInputChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              placeholder="Search use cases..."
              className="p-2 pr-10 border rounded w-full"
            />
            <button
              onClick={handleSearch}
              className="ml-2 p-2 rounded bg-blue-500 hover:bg-blue-600 text-white"
            >
              <Search size={18} />
            </button>

            {/* Status Filter Dropdown */}
            <div className="ml-2 relative" style={{ minWidth: '220px' }}>
              <div
                className="p-2 border rounded flex justify-between items-center cursor-pointer bg-white"
                onClick={() => {
                  const dropdown = document.getElementById('status-filter-dropdown');
                  if (dropdown) {
                    dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
                  }
                }}
              >
                <span className="text-sm truncate" style={{ maxWidth: '150px' }}>
                  {filterStatus.length === 0 ? 'Select statuses' :
                   filterStatus.length === availableStatuses.length ? 'All statuses' :
                   filterStatus.length === 1 ? filterStatus[0] :
                   `${filterStatus.length} statuses selected`}
                </span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              <div
                id="status-filter-dropdown"
                className="absolute left-0 right-0 mt-1 bg-white border rounded shadow-lg z-10"
                style={{ display: 'none', maxHeight: '250px', overflowY: 'auto' }}
              >
                <div className="p-2 border-b sticky top-0 bg-white">
                  <div className="flex justify-between items-center">
                    <button
                      className="text-xs text-blue-600 hover:text-blue-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFilterStatus([...availableStatuses]);
                      }}
                    >
                      Select All
                    </button>
                    <button
                      className="text-xs text-blue-600 hover:text-blue-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFilterStatus([]);
                      }}
                    >
                      Clear All
                    </button>
                  </div>
                </div>





{availableStatuses.map(status => (
  <div
    key={status}
    className="p-2 hover:bg-gray-100 cursor-pointer"
    onClick={(e) => {
      // Only handle clicks that didn't come from the checkbox itself
      if (e.target.type !== 'checkbox') {
        e.stopPropagation();
        // Prevent label's default behavior when clicking on text
        e.preventDefault();
        setFilterStatus(prev => {
          if (prev.includes(status)) {
            return prev.filter(s => s !== status);
          } else {
            return [...prev, status];
          }
        });
      }
    }}
  >
    <div className="flex items-center space-x-2 cursor-pointer">
      <input
        type="checkbox"
        checked={filterStatus.includes(status)}
        onChange={(e) => {
          e.stopPropagation();
          setFilterStatus(prev => {
            if (prev.includes(status)) {
              return prev.filter(s => s !== status);
            } else {
              return [...prev, status];
            }
          });
        }}
        className="h-4 w-4"
      />
      <span>{status}</span>
    </div>
  </div>
))}






              </div>
            </div>

            {/* Sort Dropdown */}
            <div className="ml-2 relative">
              <select
                value={sortCriteria}
                onChange={(e) => setSortCriteria(e.target.value)}
                className="p-2 border rounded bg-white text-sm"
              >
                <option value="updated_date desc">Updated Date (Newest)</option>
                <option value="updated_date asc">Updated Date (Oldest)</option>
                <option value="usecase_description asc">Description (A-Z)</option>
                <option value="usecase_description desc">Description (Z-A)</option>
                <option value="status asc">Status (A-Z)</option>
                <option value="status desc">Status (Z-A)</option>
              </select>
            </div>
          </div>

          <h2 className="text-l font-bold text-blue-700 text-center flex-1">{getLevelTitle()}</h2>

          {/* Create New Button with permissions and tooltip */}
          <div className="group relative">
            <button
              disabled={!permissions.canCreateEdit}
              onClick={() => {
                if (permissions.canCreateEdit) {
                  handleOpenCreateDialog();
                }
              }}
              className={'flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ' + (!permissions.canCreateEdit ? 'opacity-50' : '')}
              style={{
                cursor:  'pointer'
              }}
            >
              <Plus size={18} className="mr-2" />
              Create New
            </button>

            {/* Hover tooltip for disabled state */}
            {!permissions.canCreateEdit && (
              <div className="absolute right-0 top-full mt-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                Insufficient permission to create use cases
                <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-600"></div>
              </div>
            )}
          </div>
        </div>
        {/* End Title bar */}

        {/* Use Cases Table */}
        <div className="flex-1 overflow-auto border border-gray-200 rounded-md">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="py-3 px-4 text-left">
                  <input
                    type="checkbox"
                    checked={useCases.length > 0 && selectedUseCases.size === useCases.length}
                    onChange={(e) => {
                      const newSelected = new Set();
                      if (e.target.checked) {
                        useCases.forEach(useCase => newSelected.add(useCase.usecase_id));
                      }
                      setSelectedUseCases(newSelected);
                    }}
                    disabled={useCases.length === 0}
                    className="h-4 w-4"
                  />
                </th>
                <th className="py-3 px-4 text-left">
                  <div className="flex items-center cursor-pointer" onClick={() => {
                    const newSort = sortCriteria === 'usecase_id_with_prefix asc' ? 'usecase_id_with_prefix desc' : 'usecase_id_with_prefix asc';
                    setSortCriteria(newSort);
                  }}>
                    Use Case ID
                    {sortCriteria.includes('usecase_id_with_prefix') && (
                      sortCriteria.includes('asc') ? <SortAsc size={14} className="ml-1" /> : <SortDesc size={14} className="ml-1" />
                    )}
                  </div>
                </th>
                <th className="py-3 px-4 text-left">
                  <div className="flex items-center cursor-pointer" onClick={() => {
                    const newSort = sortCriteria === 'usecase_description asc' ? 'usecase_description desc' : 'usecase_description asc';
                    setSortCriteria(newSort);
                  }}>
                    Description
                    {sortCriteria.includes('usecase_description') && (
                      sortCriteria.includes('asc') ? <SortAsc size={14} className="ml-1" /> : <SortDesc size={14} className="ml-1" />
                    )}
                  </div>
                </th>
                <th className="py-3 px-4 text-left">Functional Level</th>
                <th className="py-3 px-4 text-left">
                  <div className="flex items-center cursor-pointer" onClick={() => {
                    const newSort = sortCriteria === 'status asc' ? 'status desc' : 'status asc';
                    setSortCriteria(newSort);
                  }}>
                    Status
                    {sortCriteria.includes('status') && (
                      sortCriteria.includes('asc') ? <SortAsc size={14} className="ml-1" /> : <SortDesc size={14} className="ml-1" />
                    )}
                  </div>
                </th>
                <th className="py-3 px-4 text-left">
                  <div className="flex items-center cursor-pointer" onClick={() => {
                    const newSort = sortCriteria === 'updated_date asc' ? 'updated_date desc' : 'updated_date asc';
                    setSortCriteria(newSort);
                  }}>
                    Last Updated
                    {sortCriteria.includes('updated_date') && (
                      sortCriteria.includes('asc') ? <SortAsc size={14} className="ml-1" /> : <SortDesc size={14} className="ml-1" />
                    )}
                  </div>
                </th>
                <th className="py-3 px-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="8" className="text-center py-8">Loading...</td>
                </tr>
              ) : useCases.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-8 text-gray-500">
                    No use cases found.
                  </td>
                </tr>
              ) : (
                useCases.map(useCase => (
                  <tr key={useCase.usecase_id} className={`border-b border-gray-200 hover:bg-gray-100 ${selectedUseCases.has(useCase.usecase_id) ? 'bg-blue-100' : ''}`}>
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectedUseCases.has(useCase.usecase_id)}
                        onChange={() => {
                          const newSelected = new Set(selectedUseCases);
                          if (newSelected.has(useCase.usecase_id)) {
                            newSelected.delete(useCase.usecase_id);
                          } else {
                            newSelected.add(useCase.usecase_id);
                          }
                          setSelectedUseCases(newSelected);
                        }}
                        className="h-4 w-4"
                      />
                    </td>
                    <td className="py-3 px-4" style={{ fontSize: '80%' }}>
                      <div className="group relative inline-block">
                        <a
                          href="#"
                          className={`${permissions.canCreateEdit ? 'text-blue-600 hover:underline cursor-pointer' : 'text-gray-500'}`}
                          onClick={(e) => {
                            e.preventDefault();
                            if (permissions.canCreateEdit) {
                              handleOpenEditDialog(useCase.usecase_id);
                            }
                          }}
                        >
                          {useCase.usecase_id_with_prefix}
                        </a>

                        {/* Hover tooltip for disabled state */}
                        {!permissions.canCreateEdit && (
                          <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 whitespace-nowrap z-20">
                            Insufficient permission to edit use cases
                            <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-600"></div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4" style={{ fontSize: '80%' }}>
                      {useCase.usecase_description.length > 100
                        ? `${useCase.usecase_description.substring(0, 100)}...`
                        : useCase.usecase_description}
                    </td>
                    <td className="py-3 px-4" style={{ fontSize: '80%' }}>{useCase.level_description || '-'}</td>
                    <td className="py-3 px-4" style={{ fontSize: '80%' }}>
                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                        useCase.status === 'Active' ? 'bg-green-100 text-green-800' :
                        useCase.status === 'Inactive' ? 'bg-yellow-100 text-yellow-800' :
                        useCase.status === 'Completed' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {useCase.status}
                      </span>
                    </td>
                    <td className="py-3 px-4" style={{ fontSize: '80%' }}>{new Date(useCase.updated_date).toLocaleDateString()}</td>


                  <td className="py-3 px-4">
                  <div className="group relative">
                    <button
                      className={'p-1 rounded hover:bg-[#FF8F89] flex items-center justify-center ' + (!permissions.canCreateEdit ? 'opacity-50' : '')}
                      onClick={() => {
                        if (permissions.canCreateEdit) {
                          handleOpenEditDialog(useCase.usecase_id);
                        }
                      }}
                      disabled={!permissions.canCreateEdit}
                      title="Edit Use Case"
                    >
                      <Edit2 size={16} />
                    </button>

                    {/* Hover tooltip for disabled state */}
                    {!permissions.canCreateEdit && (
                        <div className="absolute right-full mr-2 top-1/2 transform -translate-y-1/2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 whitespace-nowrap z-20">
                          Insufficient permission to edit use cases
                          <div className="absolute left-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-l-4 border-transparent border-l-gray-600"></div>
                        </div>
                    )}
                  </div>
                </td>



                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer with action buttons */}
        <div className="flex justify-between items-center mt-6 mb-4 ml-6 mr-6">
          <div className="flex gap-4">
            {/* Delete Selected Button with permissions and tooltip */}
            <div className="group relative">
              <button
                className={'bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md flex items-center ' + (
                  !permissions.canDelete || selectedUseCases.size === 0 ? 'opacity-50 ' : ''
                )}
                onClick={handleDeleteUseCasesConfirm}
                disabled={!permissions.canDelete || selectedUseCases.size === 0}
                style={{
                  cursor:   'pointer'
                }}
              >
                <Trash2 size={18} className="mr-2" />
                Delete Selected ({selectedUseCases.size})
              </button>

              {/* Delete button tooltip */}
              {(!permissions.canDelete || selectedUseCases.size === 0) && (
                <div className="absolute left-0 bottom-full mb-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                  {selectedUseCases.size === 0
                    ? "Select one or more use cases to delete"
                    : "Insufficient permission to delete use cases"
                  }
                  <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-600"></div>
                </div>
              )}
            </div>
          </div>

          <div className="text-sm text-gray-500">
            {selectedUseCases.size > 0 ? selectedUseCases.size + ' selected' : ''}
          </div>

          <button
            className="bg-[#FFFDD0] hover:bg-yellow-200 px-6 py-2 rounded-md"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>

      {/* Dialogs */}

      {/* Delete Confirmation Dialog */}
{renderDeleteConfirmDialog()}

      {showDialog && (
        <Dialog
          message={dialogMessage}
          onClose={handleDialogClose}
          onCancel={handleDialogCancel}
        />
      )}

      {/* Create/Edit Use Case Dialog */}
      {renderUseCaseFormDialog()}
    </div>
  );
};

export default ManageUseCasesDialog;